<template>
  <section class="serving-endpoint-access">
    <div class="serving-endpoint-access-title q-mb-sm">
      {{ title }}
      <runai-tooltip v-if="titleTooltip" :tooltip-text="titleTooltip" tooltip-position="right" width="520px" />
    </div>

    <section class="row">
      <span class="row col-8 gap-20" v-if="showProtocolAndPort">
        <span class="col-4 connection-protocol">
          <template v-if="protocolDisabled || disabled">
            <q-input
              aid="connection-protocol"
              label="Connection protocol"
              stack-label
              no-error-icon
              :model-value="modelValue.protocol"
              disable
            />
            <q-tooltip v-if="protocolDisabledInfo">{{ protocolDisabledInfo }}</q-tooltip>
          </template>
          <runai-select
            v-else
            :model-value="modelValue.protocol || ''"
            @update:model-value="updateSelectedProtocol"
            :options="protocolOptions"
            label="Protocol"
            stack-label
            no-error-icon
            aid="connection-protocol"
          />
        </span>
        <span class="col-3 container-port">
          <runai-number-input
            aid="container-port"
            label="Container port"
            stack-label
            no-error-icon
            :model-value="modelValue.port"
            @update:model-value="updatePort"
            :rules="[portNotEmpty, validPort]"
            :disable="portDisabled || disabled"
            :min="minValidPort"
            :max="maxValidPort"
          />
          <q-tooltip v-if="portDisabledInfo">{{ portDisabledInfo }}</q-tooltip>
        </span>
      </span>
      <span class="col-4 connection-access">
        <connection-access
          v-if="modelValue.access"
          :access="modelValue.access"
          :tool-name="'Endpoint'"
          @save="onAccessChanged"
          enable-public-access
          connection-type="endpoint"
          :disabled="disabled"
          :disabled-tooltip="disabledInfo"
        />
      </span>
    </section>
  </section>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

// cmps
import { RunaiTooltip } from "@/components/common/runai-tooltip";
import { ConnectionAccess } from "@/components/environment/connection-access";
import { RunaiSelect } from "@/components/common/runai-select";
import { RunaiNumberInput } from "@/components/common/form/runai-number-input";

// models
import { type IConnectionAccess } from "@/components/environment/connection-access";
import type { ISelectOption } from "@/models/global.model";
import { errorMessages } from "@/common/error-message.constant";
import {
  EServingEndpointAccessEvents,
  maxValidPort,
  minValidPort,
  ProtocolOptions,
  type IServingEndpointAccessData,
} from "./serving-endpoint-access.model";

export default defineComponent({
  components: {
    RunaiTooltip,
    ConnectionAccess,
    RunaiSelect,
    RunaiNumberInput,
  },
  emits: {
    [EServingEndpointAccessEvents.UpdateModelValue]: (modelValue: IServingEndpointAccessData) => modelValue,
  },
  props: {
    modelValue: {
      type: Object as PropType<IServingEndpointAccessData>,
      required: true,
    },
    title: {
      type: String as PropType<string>,
      required: true,
    },
    titleTooltip: {
      type: String as PropType<string>,
      required: false,
    },
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    protocolDisabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    portDisabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    showProtocolAndPort: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    disabledInfo: {
      type: String as PropType<string>,
      required: false,
    },
  },
  data() {
    return {
      protocolOptions: ProtocolOptions,
      maxValidPort,
      minValidPort,
    };
  },
  computed: {
    portDisabledInfo(): string | undefined {
      if (this.disabled && this.disabledInfo) return this.disabledInfo;
      if (this.portDisabled) return "The connection protocol and the container port are defined within the environment";

      return undefined;
    },
    protocolDisabledInfo(): string | undefined {
      if (this.disabled && this.disabledInfo) return this.disabledInfo;
      if (this.portDisabled) return "The connection protocol and the container port are defined within the environment";

      return undefined;
    },
  },
  methods: {
    onAccessChanged(access: IConnectionAccess): void {
      this.emitChanges({
        ...this.modelValue,
        access,
      });
    },
    updateSelectedProtocol(protocol: ISelectOption): void {
      this.emitChanges({
        ...this.modelValue,
        protocol: protocol.value?.toString(),
      });
    },
    updatePort(containerPort: number | string | null): void {
      let port: number | undefined = undefined;

      if (containerPort !== null && containerPort !== undefined && containerPort !== "") {
        port = Number(containerPort);
      }

      this.emitChanges({
        ...this.modelValue,
        port,
      });
    },
    portNotEmpty(val: number | undefined): boolean | string {
      return val !== undefined || errorMessages.ENTER_A_PORT;
    },
    validPort(val: number): boolean | string {
      const message = errorMessages.ENTER_PORT_IN_RANGE.replace("${min}", `${minValidPort}`).replace(
        "${max}",
        `${maxValidPort}`,
      );

      return (val >= minValidPort && val <= maxValidPort) || message;
    },
    emitChanges(servingEndpointAccessData: IServingEndpointAccessData): void {
      this.$emit(EServingEndpointAccessEvents.UpdateModelValue, servingEndpointAccessData);
    },
  },
});
</script>
<style lang="scss" scoped></style>
